import { supabase } from "./supabase";

/** Shape returned by `supabase.auth.mfa.listFactors().data` (verified TOTP factors in `totp`). */
export type ListedFactors = {
  all: { id: string; factor_type?: string; status?: string }[];
  totp: { id: string }[];
};

export async function listFactors(): Promise<ListedFactors> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("No MFA factor data");
  return data as ListedFactors;
}

export type EnrolledTotp = {
  id: string;
  type: "totp";
  totp: {
    qr_code: string;
    secret: string;
    uri: string;
  };
};

export async function enrollTotp(friendlyName: string): Promise<EnrolledTotp> {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName,
  });
  if (error || !data) throw new Error(error?.message ?? "Failed to enroll");
  return data as EnrolledTotp;
}

export async function verifyEnrollment(factorId: string, code: string) {
  const { data: chal, error: chalErr } = await supabase.auth.mfa.challenge({ factorId });
  if (chalErr || !chal) throw new Error(chalErr?.message ?? "Failed to challenge");
  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: chal.id,
    code,
  });
  if (error) throw new Error(error.message);
}

export async function challengeAndVerify(factorId: string, code: string) {
  return verifyEnrollment(factorId, code);
}

export async function unenrollAll(): Promise<void> {
  const data = await listFactors();
  for (const f of data.all) {
    const { error } = await supabase.auth.mfa.unenroll({ factorId: f.id });
    if (error) throw new Error(error.message);
  }
}

/** Remove abandoned TOTP enrollments so a fresh `enroll` can run. */
export async function unenrollUnverifiedTotpFactors(): Promise<void> {
  const data = await listFactors();
  for (const f of data.all) {
    if (f.factor_type === "totp" && f.status !== "verified") {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: f.id });
      if (error) throw new Error(error.message);
    }
  }
}

export async function getAAL() {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw new Error(error.message);
  return data;
}

/** First verified TOTP factor id, if any. */
export function firstVerifiedTotpFactorId(data: ListedFactors): string | undefined {
  const t = data.totp?.[0];
  return t?.id;
}

export function hasVerifiedTotp(data: ListedFactors): boolean {
  return (data.totp?.length ?? 0) > 0;
}
